import type { ThemeBrandOverride, ThemeDefinition, ThemeId } from "./types"
import { allThemes, THEME_MIGRATION_MAP } from "./themes"

export const themeRegistry: Record<string, ThemeDefinition> = Object.fromEntries(
  allThemes.map((t) => [t.id, t])
)

export const defaultThemeId: ThemeId = "clear-verdict"

export function getThemeDefinition(themeId?: ThemeId | null): ThemeDefinition {
  if (!themeId) return themeRegistry[defaultThemeId]
  if (themeRegistry[themeId]) return themeRegistry[themeId]
  // Migrate deprecated theme IDs to canonical replacements
  const migrated = THEME_MIGRATION_MAP[themeId]
  if (migrated && themeRegistry[migrated]) return themeRegistry[migrated]
  return themeRegistry[defaultThemeId]
}

export function listAvailableThemes() {
  return Object.values(themeRegistry).map(({ id, label }) => ({ id, label }))
}

/** One-time migration: if localStorage has a deprecated theme ID, update it */
export function migrateStoredThemeId() {
  if (typeof localStorage === "undefined") return
  const stored = localStorage.getItem("eca-ui-theme-id")
  if (!stored) return
  if (themeRegistry[stored]) return // already valid
  const migrated = THEME_MIGRATION_MAP[stored]
  if (migrated) {
    localStorage.setItem("eca-ui-theme-id", migrated)
  } else {
    // Unknown theme ID - reset to default
    localStorage.setItem("eca-ui-theme-id", defaultThemeId)
  }
}

export function applyBrandOverride(theme: ThemeDefinition, override?: ThemeBrandOverride | null): ThemeDefinition {
  if (!override) return theme
  return {
    ...theme,
    brand: {
      appName: override.app_name || theme.brand.appName,
      shortName: override.short_name || theme.brand.shortName,
      logoUrl: override.logo_url || theme.brand.logoUrl,
      wordmarkUrl: override.wordmark_url || theme.brand.wordmarkUrl,
      faviconUrl: override.favicon_url || theme.brand.faviconUrl,
      fontHeading: override.font_heading || theme.brand.fontHeading,
      fontBody: override.font_body || theme.brand.fontBody,
      fontMono: override.font_mono || theme.brand.fontMono,
    },
  }
}
